import React, { useState } from "react";
import { cn, parseEntityRefs } from "@/lib/utils";
import { Plus, MessageSquare, Trash2, MoreVertical, Loader2 } from "lucide-react";
import { ConversationDto } from "@/api/chat";

interface ConversationSidebarProps {
    conversations: ConversationDto[];
    selectedConversationId: string | null;
    onSelectConversation: (conversationId: string) => void;
    onCreateConversation: () => void;
    onDeleteConversation: (conversationId: string) => void;
    isLoading?: boolean;
    isCreatingConversation?: boolean;
}

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
    conversations,
    selectedConversationId,
    onSelectConversation,
    onCreateConversation,
    onDeleteConversation,
    isLoading = false,
    isCreatingConversation = false,
}) => {
    const [deleteMenuOpen, setDeleteMenuOpen] = useState<string | null>(null);

    const handleDeleteClick = (conversationId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setDeleteMenuOpen(deleteMenuOpen === conversationId ? null : conversationId);
    };

    const confirmDelete = (conversationId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onDeleteConversation(conversationId);
        setDeleteMenuOpen(null);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString + "Z");
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = diffInMs / (1000 * 60);
        const diffInHours = diffInMs / (1000 * 60 * 60);

        if (diffInMinutes < 1) return "Just now";
        if (diffInMinutes < 60) return `${Math.floor(diffInMinutes)}m ago`;
        if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
        if (diffInHours < 48) return "Yesterday";
        return date.toLocaleDateString();
    };

    return (
        <div className="w-72 bg-surface-container-lowest border-r border-outline-variant/20 flex flex-col h-full">
            {/* Header */}
            <div className="p-4 border-b border-outline-variant/20">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-headline font-black text-primary">Conversations</h2>
                    <span className="text-[10px] font-headline font-bold uppercase tracking-wider text-on-surface-variant bg-surface-container px-2 py-1 rounded-sm">
                        {conversations.length}
                    </span>
                </div>

                <button
                    onClick={onCreateConversation}
                    disabled={isLoading || isCreatingConversation}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md font-headline font-bold uppercase tracking-wider text-xs text-white hover:opacity-90 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: "#050066" }}
                >
                    {isCreatingConversation ? (
                        <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Starting...
                        </>
                    ) : (
                        <>
                            <Plus className="w-3.5 h-3.5" />
                            New conversation
                        </>
                    )}
                </button>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto p-2">
                {conversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <MessageSquare className="w-8 h-8 text-on-surface-variant mb-3" />
                        <p className="text-sm text-on-surface mb-1">No conversations yet</p>
                        <p className="text-xs text-on-surface-variant">Start a new conversation to get going</p>
                    </div>
                ) : (
                    <div className="space-y-1">
                        {conversations.map((conversation) => (
                            <div
                                key={conversation.conversationId}
                                className={cn(
                                    "group relative rounded-lg p-3 cursor-pointer transition-all",
                                    selectedConversationId === conversation.conversationId
                                        ? "bg-primary/10 border border-primary/20"
                                        : "border border-transparent hover:bg-surface-container"
                                )}
                                onClick={() => onSelectConversation(conversation.conversationId)}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <MessageSquare className={cn(
                                                "w-3.5 h-3.5 flex-shrink-0",
                                                selectedConversationId === conversation.conversationId
                                                    ? "text-primary"
                                                    : "text-on-surface-variant"
                                            )} />
                                            <h3 className={cn(
                                                "text-sm font-medium truncate",
                                                selectedConversationId === conversation.conversationId
                                                    ? "text-primary font-bold"
                                                    : "text-on-surface"
                                            )}>
                                                {parseEntityRefs(conversation.title)}
                                            </h3>
                                        </div>
                                        <div className="flex items-center justify-between text-xs text-on-surface-variant">
                                            <span>{conversation.messageCount} messages</span>
                                            <span>{formatDate(conversation.updatedAt)}</span>
                                        </div>
                                    </div>

                                    {/* Delete Menu */}
                                    <div className="relative">
                                        <button
                                            onClick={(e) => handleDeleteClick(conversation.conversationId, e)}
                                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-surface-container transition-opacity cursor-pointer"
                                        >
                                            <MoreVertical className="w-3.5 h-3.5 text-on-surface-variant" />
                                        </button>

                                        {deleteMenuOpen === conversation.conversationId && (
                                            <div className="absolute right-0 top-6 bg-surface-container-lowest border border-outline-variant/20 shadow-lg rounded-lg z-10 min-w-[140px]">
                                                <button
                                                    onClick={(e) => confirmDelete(conversation.conversationId, e)}
                                                    className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-surface-container rounded-lg transition-colors cursor-pointer font-headline font-bold uppercase tracking-wider text-[10px]"
                                                    style={{ color: "#ba0a06" }}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-outline-variant/20">
                <div className="text-[10px] font-headline font-bold uppercase tracking-wider text-on-surface-variant text-center">
                    Conversations are automatically saved
                </div>
            </div>

            {/* Overlay for delete menu */}
            {deleteMenuOpen && <div className="fixed inset-0 z-5" onClick={() => setDeleteMenuOpen(null)} />}
        </div>
    );
};
